/**
 * A campaign's published result (specification §9, §10): what was recognized, how the budget is
 * split, each creator's payout and each item's status, with the dataset file that anyone can
 * verify. A creator also gets the evidence bundle of each of their items. The platform's answers
 * that decided it, which only they and the Morcast team may read. Nothing shows until the first
 * result is published.
 */

import type { DatasetItem } from "@morcast/protocol";

import { useSession } from "../auth/session.js";
import { AddressLink, TransactionLink } from "../components/ChainLinks.js";
import { Badge, ErrorMessage, Fact, styles } from "../components/ui.js";
import { formatAmount, formatCount, formatUtc } from "../format.js";
import type { PublicCampaign } from "./campaigns.js";
import { type CampaignResult, REASONS, useCampaignResult } from "./results.js";

/** Where the dataset format and `morcast-verify` are documented. */
const DATASET_DOCS = "https://github.com/Morphora-Lab/morcast-protocol/blob/main/docs/dataset.md";

const cell = "py-2 pr-4";
const header = `${cell} font-medium`;

export function ResultCard({ campaign }: { campaign: PublicCampaign }) {
  const result = useCampaignResult(campaign);
  if (!result.isError && !result.data) {
    return null;
  }

  return (
    <section aria-labelledby="result-title" className={`mt-6 text-sm ${styles.card}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id="result-title"
          className="font-display text-lg font-semibold tracking-[-0.015em] text-zinc-950 dark:text-white"
        >
          Result
        </h2>
        {result.data && (
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {result.data.revision === 0 ? "Published" : `Revision ${result.data.revision}`}{" "}
            {formatUtc(result.data.publishedAt)}
          </span>
        )}
      </div>
      {result.isError && <ErrorMessage error={result.error} />}
      {result.data && <Published campaign={campaign} result={result.data} />}
    </section>
  );
}

/** A part of a whole as a percentage, to two decimals. */
function percent(part: string, whole: string): number {
  const total = BigInt(whole);
  return total === 0n ? 0 : Number((BigInt(part) * 10_000n) / total) / 100;
}

/**
 * The result at a glance: what was recognized against the target, and how the budget splits
 * between creators, Morcast and the brand. The figures themselves are in the list above.
 */
function ResultBars(totals: {
  recognized: string;
  target: string;
  budget: string;
  pool: string;
  fee: string;
  refund: string;
}) {
  const reached = percent(totals.recognized, totals.target);
  const parts = [
    {
      label: "Creators",
      share: percent(totals.pool, totals.budget),
      tone: "from-brand-600 to-brand-400",
    },
    {
      label: "Morcast",
      share: percent(totals.fee, totals.budget),
      tone: "from-brand-300 to-brand-200",
    },
    {
      label: "Brand's refund",
      share: percent(totals.refund, totals.budget),
      tone: "from-zinc-300 to-zinc-200 dark:from-white/20 dark:to-white/10",
    },
  ];
  return (
    <div className="mt-6 grid gap-5 rounded-xl bg-zinc-900/[0.025] p-4 ring-1 ring-zinc-900/[0.05] ring-inset sm:grid-cols-2 dark:bg-white/[0.025] dark:ring-white/[0.06]">
      <div>
        <p className="flex items-baseline justify-between gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          Recognized
          <span className="font-medium text-zinc-900 tabular-nums dark:text-white">
            {Math.min(100, Math.round(reached))}% of the target
          </span>
        </p>
        <div
          aria-hidden
          className="mt-2 h-2 overflow-hidden rounded-full bg-zinc-900/[0.07] dark:bg-white/[0.08]"
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand-600 to-brand-400"
            style={{ width: `${Math.min(100, reached)}%` }}
          />
        </div>
      </div>
      <div aria-hidden>
        <p className="text-xs text-zinc-500 dark:text-zinc-400">Budget</p>
        <div className="mt-2 flex h-2 gap-0.5 overflow-hidden rounded-full">
          {parts
            .filter((part) => part.share > 0)
            .map((part) => (
              <span
                key={part.label}
                className={`rounded-full bg-gradient-to-r ${part.tone}`}
                style={{ width: `${part.share}%` }}
              />
            ))}
        </div>
        <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
          {parts.map((part) => (
            <span key={part.label} className="inline-flex items-center gap-1.5">
              <span className={`size-2 rounded-full bg-gradient-to-r ${part.tone}`} />
              {part.label}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
}

/**
 * Where the signed-in wallet reads an item's evidence bundle: its own items', or every item's for
 * an admin. Null for the others, or for an item without evidence.
 */
function evidenceUrl(
  item: Pick<DatasetItem, "wallet" | "evidence">,
  viewer: { wallet: string; isAdmin: boolean } | null | undefined,
): string | null {
  const hash = item.evidence?.startsWith("sha256:") ? item.evidence.slice("sha256:".length) : null;
  if (!hash || !viewer || (!viewer.isAdmin && item.wallet.toLowerCase() !== viewer.wallet)) {
    return null;
  }
  return `/api/evidence/${hash}`;
}

function Published({ campaign, result }: { campaign: PublicCampaign; result: CampaignResult }) {
  const { dataset } = result;
  const { totals } = dataset;
  const { token, metric } = campaign;
  const viewer = useSession().data;

  return (
    <>
      <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-3">
        <Fact label="Recognized">
          {formatCount(totals.recognized)} of {formatCount(dataset.campaign.target)} {metric.unit}
        </Fact>
        <Fact label="Brand pays">{formatAmount(totals.spent, token)}</Fact>
        <Fact label="Brand's refund">{formatAmount(totals.refund, token)}</Fact>
        <Fact label="Creators share">{formatAmount(totals.pool, token)}</Fact>
        <Fact label="Morcast fee">{formatAmount(totals.fee, token)}</Fact>
      </dl>
      <ResultBars
        recognized={totals.recognized}
        target={dataset.campaign.target}
        budget={dataset.campaign.budget}
        pool={totals.pool}
        fee={totals.fee}
        refund={totals.refund}
      />

      <h3 className="mt-8 font-display font-semibold text-zinc-950 dark:text-white">Creators</h3>
      {dataset.creators.length === 0 ? (
        <p className="mt-2 text-zinc-600 dark:text-zinc-400">No creator took part.</p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-zinc-500 dark:text-zinc-400">
              <tr>
                <th scope="col" className={header}>
                  Wallet
                </th>
                <th scope="col" className={header}>
                  Total
                </th>
                <th scope="col" className={header}>
                  Score
                </th>
                <th scope="col" className={header}>
                  Payout
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-white/10">
              {dataset.creators.map((creator) => (
                <tr key={creator.wallet}>
                  <td className={cell}>
                    <AddressLink chainId={campaign.chainId} address={creator.wallet} short />
                  </td>
                  <td className={cell}>{formatCount(creator.total)}</td>
                  <td className={cell}>{formatCount(creator.score)}</td>
                  <td className={cell}>{formatAmount(creator.payout, token)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
        A total below {formatCount(dataset.campaign.threshold)} scores 0.
      </p>

      {dataset.items.length > 0 && (
        <>
          <h3 className="mt-8 font-display font-semibold text-zinc-950 dark:text-white">Items</h3>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-left">
              <thead className="text-zinc-500 dark:text-zinc-400">
                <tr>
                  <th scope="col" className={header}>
                    Content
                  </th>
                  <th scope="col" className={header}>
                    Creator
                  </th>
                  <th scope="col" className={header}>
                    {metric.unit.charAt(0).toUpperCase() + metric.unit.slice(1)}
                  </th>
                  <th scope="col" className={header}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-white/10">
                {dataset.items.map((item) => (
                  <tr key={item.submissionId}>
                    <td className={cell}>
                      <a href={item.contentUrl} className="break-all hover:underline">
                        {item.contentId}
                      </a>
                    </td>
                    <td className={cell}>
                      <AddressLink chainId={campaign.chainId} address={item.wallet} short />
                    </td>
                    <td className={cell}>{formatCount(item.metric)}</td>
                    <td className={cell}>
                      <ItemStatus item={item} />
                      <Evidence item={item} url={evidenceUrl(item, viewer)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="mt-6 text-zinc-600 dark:text-zinc-400">
        Every total, payout and the settlement hash follow from the items. Anyone can recompute them
        from{" "}
        <a href={result.url} download className="font-medium underline">
          the dataset file
        </a>{" "}
        with{" "}
        <a href={DATASET_DOCS} className="font-medium underline">
          morcast-verify
        </a>
        .
        {result.copyUrl && (
          <>
            {" "}
            The file also has{" "}
            <a href={result.copyUrl} className="font-medium underline">
              a permanent copy
            </a>{" "}
            that does not depend on Morcast's API.
          </>
        )}
        {result.archiveUrl && (
          <>
            {" "}
            A{" "}
            <a href={result.archiveUrl} className="font-medium underline">
              copy on Arweave
            </a>{" "}
            does not depend on Morcast at all.
          </>
        )}
      </p>
      <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Fact label="Result hash">
          <span className="font-mono text-xs break-all">{result.resultHash}</span>
        </Fact>
        {result.settlement && (
          <Fact label="Settlement">
            <SettlementState campaign={campaign} settlement={result.settlement} />
          </Fact>
        )}
        {result.revisions.length > 1 && (
          <Fact label="Earlier revisions">
            <ul className="space-y-1">
              {result.revisions
                .filter((entry) => entry.revision !== result.revision)
                .map((entry) => (
                  <li key={entry.revision}>
                    <a href={entry.url} download className="hover:underline">
                      {entry.revision === 0 ? "First publication" : `Revision ${entry.revision}`}
                    </a>{" "}
                    <span className="font-normal text-zinc-500 dark:text-zinc-400">
                      {formatUtc(entry.publishedAt)}
                    </span>
                  </li>
                ))}
            </ul>
          </Fact>
        )}
      </dl>
    </>
  );
}

/** Where the settlement stands, waiting for Morcast's settler or executed on-chain. */
function SettlementState({
  campaign,
  settlement,
}: {
  campaign: PublicCampaign;
  settlement: NonNullable<CampaignResult["settlement"]>;
}) {
  const revision =
    settlement.revision === 0 ? "the first publication" : `revision ${settlement.revision}`;
  if (settlement.status === "executed" && settlement.executedTx) {
    return (
      <span className="font-normal">
        Settled on-chain with {revision}:{" "}
        <TransactionLink chainId={campaign.chainId} hash={settlement.executedTx} />
      </span>
    );
  }
  if (settlement.status === "failed") {
    return (
      <span className="font-normal">
        The escrow's record differs from {revision}. Morcast is looking into it.
      </span>
    );
  }
  return (
    <span className="font-normal">
      {revision.charAt(0).toUpperCase() + revision.slice(1)} is waiting for Morcast's settler to
      send the settlement.
    </span>
  );
}

/** PASS, or FAIL with its primary reason and any others. */
/** A link to download an item's evidence bundle, for the wallets that may read it. */
function Evidence({ item, url }: { item: DatasetItem; url: string | null }) {
  if (!url) {
    return null;
  }
  return (
    <a
      href={url}
      download={`morcast-evidence-${item.contentId}.json`}
      className="mt-1 block text-xs font-medium text-zinc-600 underline underline-offset-4 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white"
    >
      Evidence
    </a>
  );
}

function ItemStatus({ item }: { item: DatasetItem }) {
  if (item.status === "PASS") {
    return <Badge tone="green">Pass</Badge>;
  }
  const primary = item.primaryReason ?? "";
  const others = item.reasons.filter((reason) => reason !== primary);
  return (
    <span>
      <Badge tone="red">Fail</Badge>{" "}
      <span title={primary} className="mt-1 block text-xs text-zinc-600 dark:text-zinc-400">
        {REASONS[primary] ?? primary}
      </span>
      {others.length > 0 && (
        <span className="block text-xs text-zinc-500 dark:text-zinc-400">
          Also: {others.map((reason) => REASONS[reason] ?? reason).join("; ")}
        </span>
      )}
    </span>
  );
}
